import React from 'react';
import './App.css';
import UniversalHeader from './UniversalHeader';
import { supabase } from './supabaseClient';

function formatWhen(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function initials(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
}

function propertyLine(row) {
  const rel = row.properties;
  const p = Array.isArray(rel) ? rel[0] : rel;
  if (p?.name) return p.location ? `${p.name} · ${p.location}` : p.name;
  return '—';
}

const categoryLabel = {
  general: 'General feedback',
  property: 'Property & facilities',
  service: 'Staff & service',
  noise: 'Noise or neighbours',
  other: 'Other',
};

const statusLabel = {
  new: 'New',
  read: 'Read',
  closed: 'Closed',
};

/**
 * Admin: tenant portal feedback from /tenant/feedback (table tenant_feedback).
 */
function Feedback() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [updatingId, setUpdatingId] = React.useState(null);

  const loadData = React.useCallback(async () => {
    if (!supabase) {
      setError('Supabase is not configured.');
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    const { data, error: loadError } = await supabase
      .from('tenant_feedback')
      .select(
        `
        id,
        tenant_name,
        tenant_email,
        room_label,
        category,
        subject,
        message,
        allow_contact,
        status,
        created_at,
        properties (
          name,
          location
        )
      `
      )
      .order('created_at', { ascending: false });

    if (loadError) {
      setError(
        `${loadError.message} — Run sql/add_tenant_feedback_supabase.sql in Supabase if this table is missing.`
      );
      setRows([]);
    } else {
      setRows(Array.isArray(data) ? data : []);
    }
    setLoading(false);
  }, []);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const setStatus = async (id, status) => {
    if (!supabase) return;
    setUpdatingId(id);
    try {
      const { error: uErr } = await supabase.from('tenant_feedback').update({ status }).eq('id', id);
      if (uErr) throw uErr;
      await loadData();
    } catch (e) {
      setError(e?.message || 'Could not update status.');
    } finally {
      setUpdatingId(null);
    }
  };

  const filtered = rows.filter((r) => {
    if (statusFilter !== 'all' && (r.status || 'new') !== statusFilter) return false;
    const term = search.trim().toLowerCase();
    if (!term) return true;
    const hay = `${r.tenant_name} ${r.tenant_email} ${r.subject} ${r.message} ${r.room_label}`.toLowerCase();
    return hay.includes(term);
  });

  return (
    <div className="tenants-root">
      <UniversalHeader />

      <div className="tenants-shell">
        <header className="tenants-header">
          <div>
            <h1 className="tenants-title">Tenant feedback</h1>
            <p className="tenants-subtitle">
              Filled-in feedback forms from the tenant portal (<code>/tenant/feedback</code>), stored in{' '}
              <code>tenant_feedback</code>. Run <code>sql/add_tenant_feedback_supabase.sql</code> in Supabase if this
              list is empty or errors.
            </p>
          </div>
          <div className="tenants-actions">
            <button type="button" className="btn-ghost tenants-btn" onClick={loadData} disabled={loading}>
              Refresh
            </button>
          </div>
        </header>

        {error ? (
          <p className="properties-save-error" role="alert">
            {error}
          </p>
        ) : null}

        <section className="tenants-toolbar">
          <div className="tenants-search">
            <div className="input-shell tenants-search-shell">
              <div className="input-icon">🔍</div>
              <input
                className="input-element"
                type="text"
                placeholder="Search tenant, email, or message"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="tenants-filters">
            {['all', 'new', 'read', 'closed'].map((key) => (
              <button
                key={key}
                type="button"
                className={`tenants-filter-chip ${statusFilter === key ? 'tenants-filter-chip--active' : ''}`}
                onClick={() => setStatusFilter(key)}
              >
                {key === 'all' ? 'All' : statusLabel[key]}
              </button>
            ))}
          </div>
        </section>

        <main className="tenants-layout">
          <section className="tenants-list">
            <div className="tenants-list-header">
              <span className="tenants-list-label">Submissions</span>
              <span className="tenants-list-count">{loading ? '…' : `${filtered.length} shown`}</span>
            </div>

            {loading ? (
              <p className="properties-list-empty">Loading…</p>
            ) : (
              <div className="tenants-grid">
                {filtered.length === 0 ? (
                  <p className="properties-list-empty">
                    {rows.length === 0
                      ? 'No feedback yet. Tenants submit from their portal after you run the SQL migration.'
                      : 'Nothing matches your search or filters.'}
                  </p>
                ) : (
                  filtered.map((r) => {
                    const st = r.status || 'new';
                    return (
                      <article className="tenants-card feedback-admin-card" key={r.id}>
                        <div className="tenants-card-main">
                          <div className="tenants-avatar">
                            <span>{initials(r.tenant_name)}</span>
                          </div>
                          <div className="tenants-main-text">
                            <h2 className="tenants-card-name">{r.tenant_name?.trim() || '—'}</h2>
                            <p className="tenants-card-contact">{r.tenant_email || '—'}</p>
                            <p className="tenants-card-property">
                              {propertyLine(r)}
                              {r.room_label ? ` · Room ${r.room_label}` : ''}
                            </p>
                            <p className="feedback-admin-topic">
                              <span className="feedback-admin-pill">{categoryLabel[r.category] || r.category}</span>
                              {r.subject?.trim() ? (
                                <span className="feedback-admin-subject"> · {r.subject.trim()}</span>
                              ) : null}
                            </p>
                            <p className="feedback-admin-message">{r.message || '—'}</p>
                            <p className="feedback-admin-contact-note">
                              Contact about this: <strong>{r.allow_contact ? 'Yes' : 'No'}</strong>
                            </p>
                          </div>
                          <span
                            className={`enquiries-status-badge ${
                              st === 'closed'
                                ? 'enquiries-status-badge--closed'
                                : st === 'read'
                                  ? 'enquiries-status-badge--read'
                                  : 'enquiries-status-badge--new'
                            }`}
                            style={{ alignSelf: 'flex-start' }}
                          >
                            {statusLabel[st] || st}
                          </span>
                        </div>
                        <div className="tenants-card-meta">
                          <div className="tenants-meta-block">
                            <span className="tenants-meta-label">Received</span>
                            <span className="tenants-meta-value">{formatWhen(r.created_at)}</span>
                          </div>
                        </div>
                        <div className="feedback-admin-actions">
                          {st !== 'read' && st !== 'closed' ? (
                            <button
                              type="button"
                              className="btn-ghost tenants-btn"
                              disabled={updatingId === r.id}
                              onClick={() => setStatus(r.id, 'read')}
                            >
                              {updatingId === r.id ? '…' : 'Mark read'}
                            </button>
                          ) : null}
                          {st !== 'closed' ? (
                            <button
                              type="button"
                              className="btn-primary tenants-btn"
                              disabled={updatingId === r.id}
                              onClick={() => setStatus(r.id, 'closed')}
                            >
                              {updatingId === r.id ? '…' : 'Close'}
                            </button>
                          ) : null}
                          {st === 'closed' ? (
                            <button
                              type="button"
                              className="btn-ghost tenants-btn"
                              disabled={updatingId === r.id}
                              onClick={() => setStatus(r.id, 'read')}
                            >
                              Reopen (mark read)
                            </button>
                          ) : null}
                        </div>
                      </article>
                    );
                  })
                )}
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}

export default Feedback;
